import React, { useState, useEffect } from 'react';
import { Save, Image as ImageIcon, RefreshCw, Plus, Trash2, MoveUp, MoveDown, Clock, Eye } from 'lucide-react';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import ImageUploader from './ImageUploader';

const HeroSliderManagement = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [heroData, setHeroData] = useState({
    title: 'الرفاهية تبدأ من هنا',
    subtitle: 'قطع غيار وإكسسوارات السيارات الفاخرة',
    description: 'نوفر لك أفضل قطع الغيار الأصلية والإكسسوارات الفاخرة لسيارتك مع خدمة عملاء استثنائية',
    buttonText: 'تصفح المنتجات',
    backgroundImages: [],
    backgroundColor: 'from-gray-900 via-gray-800 to-black',
    slideInterval: 5000
  });
  const [currentPreviewIndex, setCurrentPreviewIndex] = useState(0);

  useEffect(() => {
    loadHeroData();
  }, []);

  // معاينة تلقائية للصور
  useEffect(() => {
    if (heroData.backgroundImages.length > 1) {
      const interval = setInterval(() => {
        setCurrentPreviewIndex((prev) => (prev + 1) % heroData.backgroundImages.length);
      }, heroData.slideInterval || 5000);
      return () => clearInterval(interval);
    }
  }, [heroData.backgroundImages, heroData.slideInterval]);

  const loadHeroData = async () => {
    setLoading(true);
    try {
      const docRef = doc(db, 'settings', 'hero');
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        const data = docSnap.data();
        // التوافق مع البيانات القديمة
        if (data.backgroundImage && !data.backgroundImages) {
          data.backgroundImages = data.backgroundImage ? [data.backgroundImage] : [];
        }
        setHeroData({
          ...data,
          backgroundImages: data.backgroundImages || [],
          slideInterval: data.slideInterval || 5000
        });
      }
    } catch (error) {
      console.error('Error loading hero data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      const dataToSave = {
        ...heroData,
        updatedAt: new Date().toISOString()
      };

      await setDoc(doc(db, 'settings', 'hero'), dataToSave);
      alert('تم حفظ البانر بنجاح! ✅');
    } catch (error) {
      console.error('Error saving hero:', error);
      alert('حدث خطأ أثناء الحفظ');
    } finally {
      setSaving(false);
    }
  };

  const addImage = () => {
    setHeroData({
      ...heroData,
      backgroundImages: [...heroData.backgroundImages, '']
    });
  };

  const removeImage = (index) => {
    if (window.confirm('هل أنت متأكد من حذف هذه الصورة؟')) {
      const newImages = heroData.backgroundImages.filter((_, i) => i !== index);
      setHeroData({
        ...heroData,
        backgroundImages: newImages
      });
      if (currentPreviewIndex >= newImages.length) {
        setCurrentPreviewIndex(Math.max(0, newImages.length - 1));
      }
    }
  };

  const updateImage = (index, url) => {
    const newImages = [...heroData.backgroundImages];
    newImages[index] = url;
    setHeroData({
      ...heroData,
      backgroundImages: newImages
    });
  };

  const moveImageUp = (index) => {
    if (index > 0) {
      const newImages = [...heroData.backgroundImages];
      [newImages[index - 1], newImages[index]] = [newImages[index], newImages[index - 1]];
      setHeroData({
        ...heroData,
        backgroundImages: newImages
      });
    }
  };

  const moveImageDown = (index) => {
    if (index < heroData.backgroundImages.length - 1) {
      const newImages = [...heroData.backgroundImages];
      [newImages[index], newImages[index + 1]] = [newImages[index + 1], newImages[index]];
      setHeroData({
        ...heroData,
        backgroundImages: newImages
      });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="bg-gradient-to-r from-purple-500 to-pink-500 p-3 rounded-xl">
            <ImageIcon className="w-6 h-6 text-white" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-gray-900">إدارة البانر الرئيسي (Slider)</h2>
            <p className="text-gray-600">تحكم في الصور والنصوص الظاهرة في البانر</p>
          </div>
        </div>
      </div>

      {/* Preview */}
      <div className="bg-white rounded-xl shadow-md overflow-hidden">
        <div className="bg-gray-800 px-4 py-3 border-b border-gray-700 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Eye className="w-5 h-5 text-white" />
            <h3 className="text-white font-semibold">معاينة مباشرة</h3>
          </div>
          {heroData.backgroundImages.length > 1 && (
            <div className="text-white text-sm">
              الصورة {currentPreviewIndex + 1} من {heroData.backgroundImages.length}
            </div>
          )}
        </div>
        <div className={`bg-gradient-to-br ${heroData.backgroundColor} min-h-[300px] flex items-center justify-center p-8 relative overflow-hidden`}>
          {/* الصور */}
          {heroData.backgroundImages.map((image, index) => (
            image && (
              <div
                key={index}
                className={`absolute inset-0 w-full h-full transition-opacity duration-1000 ${
                  index === currentPreviewIndex ? 'opacity-100' : 'opacity-0'
                }`}
                style={{
                  backgroundImage: `url(${image})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  backgroundRepeat: 'no-repeat'
                }}
              />
            )
          ))}
          
          {/* Overlay */}
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/60"></div>
          
          {/* المحتوى */}
          <div className="relative z-10 text-center text-white max-w-3xl">
            <img src="/3.png" alt="Logo" className="w-24 h-24 mx-auto mb-4 object-contain drop-shadow-2xl" />
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-3 drop-shadow-2xl">
              {heroData.title || 'العنوان الرئيسي'}
            </h1>
            <p className="text-lg md:text-xl mb-3 text-yellow-400 drop-shadow-lg">
              {heroData.subtitle || 'العنوان الفرعي'}
            </p>
            <p className="text-base mb-6 opacity-90 drop-shadow-lg">
              {heroData.description || 'الوصف'}
            </p>
            <button className="bg-yellow-500 hover:bg-yellow-600 text-black px-6 py-3 rounded-full text-lg font-bold transition-all hover:scale-105 inline-flex items-center gap-2 shadow-xl">
              {heroData.buttonText || 'نص الزر'}
            </button>
            
            {/* Dots */}
            {heroData.backgroundImages.length > 1 && (
              <div className="flex justify-center gap-2 mt-6">
                {heroData.backgroundImages.map((_, idx) => (
                  <button
                    key={idx}
                    onClick={() => setCurrentPreviewIndex(idx)}
                    className={`h-2 rounded-full transition-all ${
                      idx === currentPreviewIndex ? 'w-8 bg-yellow-500' : 'w-2 bg-white/50'
                    }`}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Edit Form */}
      <form onSubmit={handleSave} className="bg-white rounded-xl shadow-md p-6 space-y-6">
        <h3 className="text-xl font-bold text-gray-900 mb-4">تحرير المحتوى</h3>

        {/* Title */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2 text-right">
            العنوان الرئيسي
          </label>
          <input
            type="text"
            value={heroData.title}
            onChange={(e) => setHeroData({ ...heroData, title: e.target.value })}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-right"
            required
          />
        </div>

        {/* Subtitle */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2 text-right">
            العنوان الفرعي
          </label>
          <input
            type="text"
            value={heroData.subtitle}
            onChange={(e) => setHeroData({ ...heroData, subtitle: e.target.value })}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-right"
            required
          />
        </div>

        {/* Description */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2 text-right">
            النص الوصفي
          </label>
          <textarea
            value={heroData.description}
            onChange={(e) => setHeroData({ ...heroData, description: e.target.value })}
            rows="3"
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-right"
            required
          />
        </div>

        {/* Button Text */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2 text-right">
            نص الزر
          </label>
          <input
            type="text"
            value={heroData.buttonText}
            onChange={(e) => setHeroData({ ...heroData, buttonText: e.target.value })}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-right"
            required
          />
        </div>

        {/* Background Color */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2 text-right">
            لون الخلفية (Gradient)
          </label>
          <select
            value={heroData.backgroundColor}
            onChange={(e) => setHeroData({ ...heroData, backgroundColor: e.target.value })}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-right"
          >
            <option value="from-gray-900 via-gray-800 to-black">رمادي غامق - أسود</option>
            <option value="from-blue-900 via-blue-800 to-blue-950">أزرق غامق</option>
            <option value="from-purple-900 via-purple-800 to-indigo-950">بنفسجي غامق</option>
            <option value="from-red-900 via-red-800 to-black">أحمر غامق - أسود</option>
            <option value="from-green-900 via-green-800 to-black">أخضر غامق - أسود</option>
            <option value="from-yellow-900 via-orange-800 to-red-900">ذهبي - برتقالي</option>
          </select>
        </div>

        {/* Slide Interval */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2 text-right">
            <Clock className="w-4 h-4 inline ml-2" />
            وقت التبديل بين الصور (بالثواني)
          </label>
          <input
            type="number"
            min="2"
            max="30"
            step="1"
            value={heroData.slideInterval / 1000}
            onChange={(e) => setHeroData({ ...heroData, slideInterval: parseInt(e.target.value) * 1000 })}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 text-right"
          />
          <p className="text-xs text-gray-500 mt-1 text-right">
            الوقت الحالي: {heroData.slideInterval / 1000} ثانية
          </p>
        </div>

        {/* Background Images */}
        <div className="border-t pt-6">
          <div className="flex items-center justify-between mb-4">
            <h4 className="text-lg font-bold text-gray-900">صور الخلفية</h4>
            <button
              type="button"
              onClick={addImage}
              className="flex items-center gap-2 px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg transition-colors"
            >
              <Plus className="w-4 h-4" />
              إضافة صورة
            </button>
          </div>

          {heroData.backgroundImages.length === 0 ? (
            <div className="text-center py-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
              <ImageIcon className="w-12 h-12 mx-auto text-gray-400 mb-2" />
              <p className="text-gray-600 mb-4">لا توجد صور بعد</p>
              <button
                type="button"
                onClick={addImage}
                className="bg-purple-500 hover:bg-purple-600 text-white px-6 py-2 rounded-lg transition-colors"
              >
                إضافة أول صورة
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              {heroData.backgroundImages.map((image, index) => (
                <div key={index} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <span className="bg-purple-500 text-white px-3 py-1 rounded-full text-sm font-bold">
                        {index + 1}
                      </span>
                      <span className="text-sm text-gray-600">الصورة رقم {index + 1}</span>
                    </div>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => moveImageUp(index)}
                        disabled={index === 0}
                        className="p-2 bg-blue-500 hover:bg-blue-600 disabled:bg-gray-300 text-white rounded-lg transition-colors"
                        title="تحريك لأعلى"
                      >
                        <MoveUp className="w-4 h-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => moveImageDown(index)}
                        disabled={index === heroData.backgroundImages.length - 1}
                        className="p-2 bg-blue-500 hover:bg-blue-600 disabled:bg-gray-300 text-white rounded-lg transition-colors"
                        title="تحريك لأسفل"
                      >
                        <MoveDown className="w-4 h-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => removeImage(index)}
                        className="p-2 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors"
                        title="حذف الصورة"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  
                  <ImageUploader
                    currentImage={image}
                    onImageChange={(url) => updateImage(index, url)}
                    label={`الصورة ${index + 1}`}
                  />
                </div>
              ))}
            </div>
          )}

          <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg text-right">
            <p className="text-sm text-blue-800 font-semibold mb-2">💡 نصائح لإدارة الصور:</p>
            <ul className="text-xs text-blue-700 space-y-1 list-disc list-inside">
              <li>استخدم 3-5 صور للحصول على أفضل تجربة مستخدم</li>
              <li>تأكد من أن جميع الصور بنفس الأبعاد (1920×1080 موصى به)</li>
              <li>استخدم زر الترتيب لتغيير ترتيب عرض الصور</li>
              <li>سيتم التبديل بين الصور تلقائياً حسب الوقت المحدد</li>
              <li>إذا كانت هناك صورة واحدة فقط، لن يظهر الـ slider</li>
            </ul>
          </div>
        </div>

        {/* Save Button */}
        <div className="flex gap-3 pt-4">
          <button
            type="submit"
            disabled={saving}
            className="flex-1 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white py-3 rounded-lg font-medium transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
          >
            {saving ? (
              <>
                <RefreshCw className="w-5 h-5 animate-spin" />
                جاري الحفظ...
              </>
            ) : (
              <>
                <Save className="w-5 h-5" />
                حفظ التغييرات
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default HeroSliderManagement;
